import {
  toModularTransport,
  toCircleSmartAccount,
  encodeTransfer,
  getUserOperationGasPrice,
  ContractAddress,
} from "@circle-fin/modular-wallets-core";
import { createPublicClient, custom, erc20Abi, formatUnits, parseUnits } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import {
  createBundlerClient,
  formatUserOperationRequest,
  getPaymasterData,
  getPaymasterStubData,
  toWebAuthnAccount,
} from "viem/account-abstraction";
import { arcTestnet } from "../chains";
import { loginCredential, registerCredential } from "../passkeyClient";
import { PREPARED_TTL_MS, SPONSOR_TTL_MS, type ExportedOp, type PreparedOp, type Submitted } from "./WalletAdapter";
import { opHash } from "../cardApproval"; // 1.5.42
import { recordMeasure } from "../diag";
import type {
  ConnectMode,
  WalletAdapter,
  WalletCredential,
} from "./WalletAdapter";

const clientKey = import.meta.env.VITE_CLIENT_KEY;
const clientUrl = import.meta.env.VITE_CLIENT_URL;
const explorerBase =
  import.meta.env.VITE_EXPLORER_BASE ?? "https://testnet.arcscan.app";

// Use the SDK enum when available; fall back to the pinned Arc testnet USDC
// address (spec §3). USDC is 6 decimals.
const USDC =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ((ContractAddress as any)?.ArcTestnet_USDC as `0x${string}` | undefined) ??
  "0x3600000000000000000000000000000000000000";
const USDC_DECIMALS = 6;

// v0.80.28 — the token a currency maps to. USDC and EURC are both native to
// Arc; the registry in fx.ts is the single list, this just resolves it.
import { currency as currencyMeta } from "../fx";
function tokenFor(code?: string): { address: `0x${string}`; decimals: number } {
  const t = code ? currencyMeta(code).token : undefined;
  return t ?? { address: USDC, decimals: USDC_DECIMALS };
}

// 1.5.7 — our own gas price is gone. v2.0 put it behind ?gas=own (kept in this
// browser's storage as m.gas); tested 22 Oct and rejected as a default, but the
// switch stayed and Alexander's Safari kept it. On 24 Oct Arc's market rose to
// ~500 gwei and every op at our ~41 gwei sat unmined: "Confirming on Circle
// Arc…" with no end. Circle's *medium* is the only price; a remembered "own" is
// forgotten on load. (decisions-log, the v2.2 block.)
try { localStorage.removeItem("m.gas"); } catch { /* storage refused — nothing remembered */ }
const gwei = (n: bigint) => (Number(n) / 1e9).toFixed(n < 10_000_000_000n ? 2 : 0);

/**
 * CircleArcAdapter — the web modular-wallet flow is browser-driven (spec §0/§3):
 * the passkey signs and the browser submits UserOps directly through Circle's
 * hosted bundler + paymaster. There is no server relayer.
 */
export class CircleArcAdapter implements WalletAdapter {
  // One modular transport → Circle bundler + paymaster for Arc testnet.
  private readonly modularTransport = toModularTransport(
    `${clientUrl}/arcTestnet`,
    clientKey,
  );

  // Public client for reads (balanceOf) over the same transport — no separate
  // Arc RPC needed.
  private readonly publicClient = createPublicClient({
    chain: arcTestnet,
    transport: this.modularTransport,
  });

  private smartAccount: Awaited<
    ReturnType<typeof toCircleSmartAccount>
  > | null = null;

  private bundlerClient: ReturnType<typeof createBundlerClient> | null = null;

  /** v2.0 — the sponsorship asked for each prepared op, and when. */
  private readonly sponsorships = new WeakMap<PreparedOp, { at: number; done: Promise<Record<string, unknown>> }>();
  private sponsorCount = 0;

  async createOrConnect(mode: ConnectMode, userLabel?: string, displayName?: string | null, pin?: string) {
    // 1. Passkey ceremony (Face ID / Touch ID, WebAuthn secp256r1). This is the
    //    "which wallet are you" prompt — one biometric.
    const credential =
      mode === "register"
        ? await registerCredential(userLabel ?? "M_wallet", displayName)
        : await loginCredential(pin);

    // 2. Derive the MSCA. Address is deterministic → login reproduces the same
    //    wallet (our "reconnect", stronger than an indexer).
    await this.build(toWebAuthnAccount({ credential }));

    return {
      address: this.smartAccount!.address,
      credential: {
        id: credential.id,
        publicKey: credential.publicKey as string,
        // rpId is present on the SDK credential; keep it for reconstruction.
        rpId: (credential as { rpId?: string }).rpId,
        userHandle: (credential as { userHandle?: string }).userHandle,
      } satisfies WalletCredential,
    };
  }

  // Rebuild the account from cached PUBLIC credential values — NO ceremony, no
  // biometric. The only remaining prompt is the signature in transfer().
  async connectWithCredential(credential: WalletCredential) {
    await this.build(
      toWebAuthnAccount({
        credential: {
          id: credential.id,
          publicKey: credential.publicKey as `0x${string}`,
        },
      }),
    );
    return { address: this.smartAccount!.address };
  }

  connectedAddress(): string | null { return this.smartAccount?.address ?? null; } // 1.5.58

  // Shared: derive the smart account from an owner + wire the bundler client.
  private async build(owner: Parameters<typeof toCircleSmartAccount>[0]["owner"]) {
    this.smartAccount = await toCircleSmartAccount({
      client: this.publicClient,
      owner,
    });
    this.bundlerClient = createBundlerClient({
      account: this.smartAccount,
      chain: arcTestnet,
      transport: this.modularTransport,
    });
  }

  async transfer(to: string, amount: string, code?: string) {
    const s = await this.submitTransfer(to, amount, code);
    const r = await s.settled;
    if (!r.success) throw new Error("userop_reverted");
    return { txHash: r.txHash };
  }

  /**
   * v0.90.26 — submit and return at acceptance. Face ID is prompted at
   * signing inside sendUserOperation; the paymaster covers gas. The receipt is
   * followed in `settled` with a short poll (500 ms for the first 3 s, then
   * 2 s) instead of viem's 4 s default, which used to tell M about a 1–2 s
   * inclusion up to 4 s late.
   */
  async submitTransfer(to: string, amount: string, code?: string): Promise<Submitted> {
    if (!this.smartAccount || !this.bundlerClient) {
      throw new Error("Wallet not connected — call createOrConnect first.");
    }
    const tok = tokenFor(code);
    const call = encodeTransfer(to as `0x${string}`, tok.address, parseUnits(amount, tok.decimals));
    // v0.80.12 — explicit, floor-clamped fees on every path.
    const { maxFeePerGas, maxPriorityFeePerGas } = await this.gasPrices();
    const hash = await this.bundlerClient.sendUserOperation({
      account: this.smartAccount,
      calls: [call],
      paymaster: true,
      maxFeePerGas,
      maxPriorityFeePerGas,
    });
    return { userOpHash: hash, settled: this.settle(hash) };
  }

  async submit(calls: { to: string; data: string }[]): Promise<Submitted> {
    if (!this.smartAccount || !this.bundlerClient) {
      throw new Error("Wallet not connected — call createOrConnect first.");
    }
    const { maxFeePerGas, maxPriorityFeePerGas } = await this.gasPrices();
    const hash = await this.bundlerClient.sendUserOperation({
      account: this.smartAccount,
      calls: calls.map((c) => ({ to: c.to as `0x${string}`, data: c.data as `0x${string}` })),
      paymaster: true,
      maxFeePerGas,
      maxPriorityFeePerGas,
    });
    return { userOpHash: hash, settled: this.settle(hash) };
  }

  /**
   * v0.90.29 — the round trips, without the signature. viem's
   * prepareUserOperation fills nonce, gas, paymaster and fees; `parameters`
   * leaves out 'signature' so no stub is attached. Nothing here prompts.
   *
   * v2.0 — with the paymaster's STUB only. Circle's pm_getPaymasterStubData
   * reserves nothing (measured 22 Oct, no Pending row) and gives the gas
   * estimate what it needs; the data call — the sponsorship, which reserves
   * every gas limit × maxFeePerGas until the next 00:00 UTC — waits for
   * sponsor(), on the touch. So preparing ahead, again or for a variant
   * nobody sends costs Circle's cap nothing.
   */
  async prepare(calls: { to: string; data: string }[]): Promise<PreparedOp> {
    if (!this.smartAccount || !this.bundlerClient) {
      throw new Error("Wallet not connected — call createOrConnect first.");
    }
    const bundler = this.bundlerClient;
    const { maxFeePerGas, maxPriorityFeePerGas } = await this.gasPrices();
    const request = await bundler.prepareUserOperation({
      account: this.smartAccount,
      calls: calls.map((c) => ({ to: c.to as `0x${string}`, data: c.data as `0x${string}` })),
      paymaster: {
        getPaymasterStubData: (a: Parameters<typeof getPaymasterStubData>[1]) => getPaymasterStubData(bundler, a),
        // Left for sponsor(): the stub's fields stay on the op until then.
        getPaymasterData: async () => ({}) as Awaited<ReturnType<typeof getPaymasterData>>,
      },
      maxFeePerGas,
      maxPriorityFeePerGas,
      parameters: ["factory", "fees", "gas", "paymaster", "nonce", "authorization"],
    });
    return { request, at: Date.now() };
  }

  async sponsor(p: PreparedOp): Promise<void> {
    await this.sponsorship(p);
  }

  /** The prepared op with the paymaster's signature: the one asked for on
   *  touch if it is under SPONSOR_TTL_MS old, else a new one. The nonce is
   *  read in parallel; a moved nonce (the prepared op's sequence already used)
   *  is "prepared_stale", and the caller takes the plain path.
   *
   *  1.4.80 — read for the prepared op's OWN nonce key. ERC-4337 nonces are
   *  two-dimensional, (key << 64) | sequence, and viem's smart account takes a
   *  fresh key (Date.now()) on every getNonce() call without one — so a bare
   *  getNonce() here never equalled the prepare's nonce, every sponsorship
   *  threw "prepared_stale" after Circle had already signed it (a Pending row
   *  nobody used), and the slide sponsored a second op on the plain path
   *  (Alexander's Console, 22 Oct 13:05: one Pending, one Settled, one payment). */
  private sponsorship(p: PreparedOp): Promise<Record<string, unknown>> {
    if (!this.smartAccount || !this.bundlerClient) {
      return Promise.reject(new Error("Wallet not connected — call createOrConnect first."));
    }
    const had = this.sponsorships.get(p);
    if (had && Date.now() - had.at < SPONSOR_TTL_MS) return had.done;
    const account = this.smartAccount;
    const bundler = this.bundlerClient;
    const req = p.request as Record<string, unknown>;
    const t0 = Date.now();
    const done = (async () => {
      const [pm, nonce] = await Promise.all([
        getPaymasterData(bundler, {
          chainId: arcTestnet.id,
          entryPointAddress: account.entryPoint.address,
          ...(req as object),
        } as Parameters<typeof getPaymasterData>[1]),
        (typeof req.nonce === "bigint" ? account.getNonce({ key: req.nonce >> 64n }) : Promise.resolve(null)).catch(() => null), // unread → the bundler is the judge
      ]);
      if (nonce !== null && typeof req.nonce === "bigint" && nonce !== req.nonce) throw new Error("prepared_stale");
      this.sponsorCount++;
      recordMeasure("sponsor", `${Date.now() - t0} ms · ${this.sponsorCount} this session · Circle's price`);
      return { ...req, ...pm } as Record<string, unknown>;
    })();
    this.sponsorships.set(p, { at: t0, done });
    done.catch(() => {
      if (this.sponsorships.get(p)?.done === done) this.sponsorships.delete(p);
    });
    return done;
  }

  /** 1.5.42 — the op the M® card will sign: sponsored here (Circle's paymaster signs it; the nonce checked), then
   *  formatted as the bundler would receive it, hashed as the EntryPoint will. */
  async exportSponsored(p: PreparedOp): Promise<ExportedOp> {
    if (!this.smartAccount) throw new Error("Wallet not connected — call createOrConnect first.");
    const request = await this.sponsorship(p);
    const rpc = formatUserOperationRequest(request as Parameters<typeof formatUserOperationRequest>[0]) as unknown as Record<string, `0x${string}` | undefined>;
    const op = {
      sender: rpc.sender!, nonce: rpc.nonce!, callData: rpc.callData!,
      callGasLimit: rpc.callGasLimit!, verificationGasLimit: rpc.verificationGasLimit!, preVerificationGas: rpc.preVerificationGas!,
      maxFeePerGas: rpc.maxFeePerGas!, maxPriorityFeePerGas: rpc.maxPriorityFeePerGas!,
      paymaster: rpc.paymaster, paymasterVerificationGasLimit: rpc.paymasterVerificationGasLimit, paymasterPostOpGasLimit: rpc.paymasterPostOpGasLimit, paymasterData: rpc.paymasterData,
      factory: rpc.factory, factoryData: rpc.factoryData,
    };
    const x = { chainId: arcTestnet.id, entryPoint: this.smartAccount.entryPoint.address, op, hash: "0x" as `0x${string}` };
    x.hash = opHash(x);
    return x;
  }

  /** 1.5.42 — after the card: the passkey signs the exported op as it is (Face ID), and the bundler takes it. */
  async sendExported(x: ExportedOp): Promise<Submitted> {
    return this.sendSigned(x, await this.signExported(x));
  }
  /** The exported op (hex strings) back in viem's request shape, for the account that holds the passkey. */
  private exportedRequest(x: ExportedOp) {
    if (!this.smartAccount || !this.bundlerClient) throw new Error("Wallet not connected — call createOrConnect first.");
    if (x.op.sender.toLowerCase() !== this.smartAccount.address.toLowerCase()) throw new Error("card_other_wallet");
    const big = (h?: `0x${string}`) => (h === undefined ? undefined : BigInt(h));
    const o = x.op;
    return {
      sender: o.sender, nonce: big(o.nonce), callData: o.callData,
      callGasLimit: big(o.callGasLimit), verificationGasLimit: big(o.verificationGasLimit), preVerificationGas: big(o.preVerificationGas),
      maxFeePerGas: big(o.maxFeePerGas), maxPriorityFeePerGas: big(o.maxPriorityFeePerGas),
      paymaster: o.paymaster, paymasterVerificationGasLimit: big(o.paymasterVerificationGasLimit), paymasterPostOpGasLimit: big(o.paymasterPostOpGasLimit), paymasterData: o.paymasterData,
      factory: o.factory, factoryData: o.factoryData,
    };
  }
  // 1.5.47 — the passkey's signature alone (Face ID), on the Pay tap, before the trip to the approver
  async signExported(x: ExportedOp): Promise<`0x${string}`> {
    const request = this.exportedRequest(x);
    return this.smartAccount!.signUserOperation(request as Parameters<typeof this.smartAccount.signUserOperation>[0]);
  }
  // 1.5.47 — the signed op to the bundler, no prompt (after the card's signature came back)
  async sendSigned(x: ExportedOp, signature: `0x${string}`): Promise<Submitted> {
    const request = this.exportedRequest(x);
    if (!this.smartAccount || !this.bundlerClient) throw new Error("Wallet not connected — call createOrConnect first.");
    const rpcParameters = formatUserOperationRequest({ ...request, signature } as Parameters<typeof formatUserOperationRequest>[0]);
    const hash = (await this.bundlerClient.request({
      method: "eth_sendUserOperation",
      params: [rpcParameters, this.smartAccount.entryPoint.address],
    } as never)) as `0x${string}`;
    return { userOpHash: hash, settled: this.settle(hash) };
  }

  async prepareTransfer(to: string, amount: string, code?: string): Promise<PreparedOp> {
    const tok = tokenFor(code);
    const call = encodeTransfer(to as `0x${string}`, tok.address, parseUnits(amount, tok.decimals));
    return this.prepare([{ to: call.to, data: call.data as string }]);
  }

  /**
   * v0.90.29 — sign (Face ID) and hand the prepared userOp straight to the
   * bundler. Bypasses viem's sendUserOperation, which would re-run the
   * preparation; this is the raw eth_sendUserOperation with the same
   * formatting viem uses.
   */
  async sendPrepared(p: PreparedOp): Promise<Submitted> {
    if (!this.smartAccount || !this.bundlerClient) {
      throw new Error("Wallet not connected — call createOrConnect first.");
    }
    if (Date.now() - p.at > PREPARED_TTL_MS) throw new Error("prepared_stale");
    // v2.0 — the sponsorship asked for on touch (done by now, usually), or asked for here.
    const request = (await this.sponsorship(p)) as Parameters<typeof this.smartAccount.signUserOperation>[0];
    const signature = await this.smartAccount.signUserOperation(request);
    const rpcParameters = formatUserOperationRequest({ ...(request as object), signature } as Parameters<typeof formatUserOperationRequest>[0]);
    const hash = (await this.bundlerClient.request({
      method: "eth_sendUserOperation",
      params: [rpcParameters, this.smartAccount.entryPoint.address],
    } as never)) as `0x${string}`;
    return { userOpHash: hash, settled: this.settle(hash) };
  }

  /** Poll the bundler for the receipt: 500 ms × 6, then every 2 s, up to 3 min. */
  private async settle(hash: `0x${string}`): Promise<{ txHash: string; success: boolean }> {
    const t0 = Date.now();
    let n = 0;
    for (;;) {
      const r = await this.bundlerClient!.getUserOperationReceipt({ hash }).catch(() => null);
      if (r) return { txHash: r.receipt.transactionHash, success: r.success };
      if (Date.now() - t0 > 180_000) throw new Error("userop_receipt_timeout");
      await new Promise((res) => setTimeout(res, n++ < 6 ? 500 : 2000));
    }
  }

  /** Arc's floor is 1 gwei on the priority fee; the bundler's own numbers
   *  are advisory and have been 0. Clamp, and keep maxFee ≥ priority.
   *  Circle's *medium*, always (1.5.7: the ?gas=own switch removed). */
  private async gasPrices(): Promise<{ maxFeePerGas: bigint; maxPriorityFeePerGas: bigint }> {
    const FLOOR = 1_000_000_000n; // 1 gwei
    let max = FLOOR;
    let prio = FLOOR;
    try {
      const gp = await getUserOperationGasPrice(this.bundlerClient!);
      max = BigInt(gp.medium.maxFeePerGas);
      prio = BigInt(gp.medium.maxPriorityFeePerGas);
    } catch (e) {
      console.warn("[M] gas price lookup failed; using the floor", e);
    }
    if (prio < FLOOR) prio = FLOOR;
    if (max < prio) max = prio;
    recordMeasure("gas price", `Circle's medium · max ${gwei(max)} gwei · priority ${gwei(prio)}`);
    return { maxFeePerGas: max, maxPriorityFeePerGas: prio };
  }

  async getBalance(address: string, code?: string) {
    const raw = (await this.publicClient.readContract({
      address: tokenFor(code).address,
      abi: erc20Abi,
      functionName: "balanceOf",
      args: [address as `0x${string}`],
    })) as bigint;
    return formatUnits(raw, tokenFor(code).decimals);
  }

  // v0.30 — generic uint read for the M-MMF contracts (balanceOf, index()).
  async readUint(
    contract: string,
    abi: readonly unknown[],
    functionName: string,
    args: readonly unknown[],
  ) {
    return (await this.publicClient.readContract({
      address: contract as `0x${string}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      abi: abi as any,
      functionName,
      args: args as readonly unknown[],
    })) as bigint;
  }

  // v0.30 — sign & submit a batch of raw contract calls as one UserOp. Same
  // path as transfer(): one Face ID at signing, paymaster covers gas.
  async execute(calls: { to: string; data: string }[]) {
    // Waits for the receipt — swaps show the settled figure. Gas rules as in
    // submit(): Circle's levels, clamped to Arc's 1 gwei floor (v0.80.12).
    const s = await this.submit(calls);
    const r = await s.settled;
    if (!r.success) throw new Error("userop_reverted");
    return { txHash: r.txHash };
  }

  // v1.3.10 — the spare key (src/sparekey). See WalletAdapter for the contract.
  async read(contract: string, abi: readonly unknown[], functionName: string, args: readonly unknown[]) {
    return this.publicClient.readContract({
      address: contract as `0x${string}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      abi: abi as any,
      functionName,
      args: args as readonly unknown[],
    });
  }

  async submitRaw(callData: string): Promise<Submitted> {
    if (!this.smartAccount || !this.bundlerClient) {
      throw new Error("Wallet not connected — call createOrConnect first.");
    }
    const { maxFeePerGas, maxPriorityFeePerGas } = await this.gasPrices();
    const hash = await this.bundlerClient.sendUserOperation({
      account: this.smartAccount,
      callData: callData as `0x${string}`,
      paymaster: true,
      maxFeePerGas,
      maxPriorityFeePerGas,
    });
    return { userOpHash: hash, settled: this.settle(hash) };
  }

  async submitWithKey(privateKey: string, calls: { to: string; data: string }[]): Promise<Submitted> {
    if (!this.smartAccount || !this.bundlerClient) {
      throw new Error("Wallet not connected — call createOrConnect first.");
    }
    // The same wallet, signed for by the ordinary key. The reads go through
    // Circle as always, but over a transport the SDK does not recognise as
    // its own: given its own transport, toCircleSmartAccount would first ask
    // Circle to register a NEW wallet for this key (circle_getAddress).
    const reader = createPublicClient({
      chain: arcTestnet,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transport: custom({ request: (a) => this.publicClient.request(a as any) }),
    });
    const account = await toCircleSmartAccount({
      client: reader,
      owner: privateKeyToAccount(privateKey as `0x${string}`),
      address: this.smartAccount.address,
    });
    const bundler = createBundlerClient({ account, chain: arcTestnet, transport: this.modularTransport });
    const { maxFeePerGas, maxPriorityFeePerGas } = await this.gasPrices();
    const hash = await bundler.sendUserOperation({
      account,
      calls: calls.map((c) => ({ to: c.to as `0x${string}`, data: c.data as `0x${string}` })),
      paymaster: true,
      maxFeePerGas,
      maxPriorityFeePerGas,
      // An ordinary signature checks in ~45k gas. Stated, because the SDK's own
      // default looks at the wrong address for this account and assumes an
      // undeployed wallet (1.5M).
      verificationGasLimit: 300_000n,
    });
    return { userOpHash: hash, settled: this.settle(hash) };
  }

  explorerUrl(txHash: string) {
    return `${explorerBase}/tx/${txHash}`;
  }
}
